<script setup>
import { computed, ref, shallowRef, watch } from 'vue';
import moment from 'moment';
import PageTabs from '@/components/Utils/PageTabs.vue';
import UiBadge from '@/components/ui/UiBadge.vue';
import UiDetailList from '@/components/ui/UiDetailList.vue';
import UiDialog from '@/components/ui/UiDialog.vue';
import UiFormFields from '@/components/ui/UiFormFields.vue';
import UiProfileHeader from '@/components/ui/UiProfileHeader.vue';
import { apiRequest } from '@/api/http';
import { useAsyncAction } from '@/composables/useAsyncAction';
import { email, required } from '@/utils/validation';
import { formatDate, titleCase } from '@/utils/format';

// A student's details from the Students list: a profile header (photo,
// name, class, LRN, status) over tabs for personal, contact and address, and
// family details. Edit turns the tabs into a form for correcting them.
// Enrolment status and grade are handled elsewhere.
const props = defineProps({
  // Row from the student list (enroll_student columns plus room_name).
  student: { type: Object, default: null },
});
const open = defineModel({ type: Boolean, default: false });
const emit = defineEmits(['saved']);

const lrn = (v) => !v || /^\d{12}$/.test(v) || 'The LRN must be 12 digits.';

const SECTIONS = [
  {
    tab: 'personal',
    title: 'Personal',
    fields: [
      { key: 'fname', label: 'First name', required: true, autocomplete: 'off' },
      { key: 'mname', label: 'Middle name', autocomplete: 'off' },
      { key: 'lname', label: 'Last name', required: true, autocomplete: 'off' },
      { key: 'suffix', label: 'Suffix', placeholder: 'e.g. Jr.', autocomplete: 'off' },
      { key: 'lrnNo', label: 'LRN', rules: [lrn], placeholder: '12 digits', autocomplete: 'off' },
      { key: 'sex', label: 'Sex', type: 'select', items: ['Male', 'Female'] },
      { key: 'bdate', label: 'Birth date', type: 'date' },
      { key: 'birth_place', label: 'Birth place', autocomplete: 'off' },
    ],
  },
  {
    tab: 'contact',
    title: 'Contact',
    fields: [
      { key: 'email', label: 'Email', rules: [email], autocomplete: 'off' },
      { key: 'mobile_no', label: 'Mobile number', autocomplete: 'off' },
    ],
  },
  {
    tab: 'contact',
    title: 'Home address',
    fields: [
      { key: 'residential_house_no', label: 'House no.', autocomplete: 'off' },
      { key: 'residential_street', label: 'Street', autocomplete: 'off' },
      { key: 'residential_brgy', label: 'Barangay', autocomplete: 'off' },
      { key: 'residential_city', label: 'City / municipality', autocomplete: 'off' },
      { key: 'residential_prov', label: 'Province', span: 2, autocomplete: 'off' },
    ],
  },
  {
    tab: 'family',
    title: 'Parents and guardian',
    fields: [
      { key: 'father_fname', label: "Father's first name", autocomplete: 'off' },
      { key: 'father_lname', label: "Father's last name", autocomplete: 'off' },
      { key: 'father_number', label: "Father's contact number", span: 2, autocomplete: 'off' },
      { key: 'mother_fname', label: "Mother's first name", autocomplete: 'off' },
      { key: 'mother_lname', label: "Mother's last name", autocomplete: 'off' },
      { key: 'mother_number', label: "Mother's contact number", span: 2, autocomplete: 'off' },
      { key: 'guardian_fname', label: "Guardian's first name", autocomplete: 'off' },
      { key: 'guardian_lname', label: "Guardian's last name", autocomplete: 'off' },
      { key: 'guardian_number', label: "Guardian's contact number", span: 2, autocomplete: 'off' },
    ],
  },
];
const KEYS = SECTIONS.flatMap((s) => s.fields.map((f) => f.key));
const TABS = [
  { id: 'personal', name: 'Personal' },
  { id: 'contact', name: 'Contact & address' },
  { id: 'family', name: 'Family' },
];
const API = process.env.VUE_APP_SERVER;

// Placeholder values the enrolment form stored for "none".
const blank = (v) => {
  const s = String(v ?? '').trim();
  return s === '' || s.toLowerCase() === 'n/a' ? '' : s;
};

const editing = shallowRef(false);
const tab = shallowRef('personal');
const form = ref({});
const formRef = ref(null);
const { busy, run } = useAsyncAction();

function reset() {
  const s = props.student || {};
  form.value = Object.fromEntries(KEYS.map((k) => [k, blank(s[k])]));
  // The date input needs YYYY-MM-DD; older records may hold other formats.
  const d = moment(form.value.bdate, ['YYYY-MM-DD', 'MM/DD/YYYY', moment.ISO_8601], true);
  form.value.bdate = d.isValid() ? d.format('YYYY-MM-DD') : '';
}

watch(open, (isOpen) => {
  if (isOpen) {
    editing.value = false;
    tab.value = 'personal';
  }
});

// The form is filled from the student when editing starts, not when the
// dialog opens: by then the selected row is certainly the one shown.
function startEdit() {
  reset();
  editing.value = true;
}

const fullName = computed(() => {
  const s = props.student || {};
  const middle = blank(s.mname);
  return titleCase(
    [blank(s.fname), middle && `${middle[0]}.`, blank(s.lname), blank(s.suffix)].filter(Boolean).join(' '),
  );
});

const photo = computed(() => {
  const name = blank(props.student?.picture);
  return name && !name.startsWith('default')
    ? `${API}/enroll-student/view/studentFile/${encodeURIComponent(name)}`
    : '';
});
const classLine = computed(() =>
  [props.student?.grade_level, props.student?.room_name].filter(Boolean).join(' · '),
);

const displayValue = (field, value) => {
  const v = blank(value);
  if (!v) return '';
  if (field.key === 'bdate') return formatDate(v);
  if (field.key === 'email' || field.key === 'lrnNo' || /number|mobile/.test(field.key)) return v;
  return titleCase(v);
};
const details = computed(() =>
  SECTIONS.map((section) => ({
    tab: section.tab,
    title: section.title,
    items: section.fields.map((f) => ({
      label: f.label,
      value: displayValue(f, props.student?.[f.key]),
      span: f.span,
    })),
  })),
);

async function save() {
  const { valid } = await formRef.value.validate();
  if (!valid) {
    // Every tab's fields stay mounted, so a problem may be on a hidden tab:
    // show the first tab that has one.
    const broken = SECTIONS.find((section) =>
      section.fields.some((f) =>
        [...(f.required ? [required] : []), ...(f.rules || [])].some((rule) => rule(form.value[f.key]) !== true),
      ),
    );
    if (broken) tab.value = broken.tab;
    return;
  }
  // Blank optional fields go up as null so the server's format checks skip them.
  const body = Object.fromEntries(
    KEYS.map((k) => {
      const v = String(form.value[k] ?? '').trim();
      return [k, v === '' ? null : v];
    }),
  );
  const ok = await run(() => apiRequest('patch', `/enroll-student/student-details/${props.student.id}`, body), {
    success: 'Student details saved.',
    error: 'Could not save the student details.',
  });
  if (ok) {
    open.value = false;
    emit('saved');
  }
}

function cancelEdit() {
  reset();
  editing.value = false;
}
</script>

<template>
  <UiDialog v-model="open" :title="fullName || 'Student details'" size="lg" :persistent="editing">
    <template #header>
      <UiProfileHeader :name="fullName || 'Student details'" :photo="photo" :subtitle="classLine">
        <UiBadge v-if="student?.lrnNo" variant="outline">LRN {{ student.lrnNo }}</UiBadge>
        <UiBadge v-if="student?.statusEnrolled != null" :variant="student.statusEnrolled == 2 ? 'amber' : 'green'">
          {{ student.statusEnrolled == 2 ? 'Dropped' : 'Enrolled' }}
        </UiBadge>
        <UiBadge v-if="editing" variant="blue">Editing</UiBadge>
      </UiProfileHeader>
    </template>

    <PageTabs v-model="tab" :items="TABS" class="details-tabs" />

    <div v-if="!editing" class="details-panel">
      <template v-for="section in details" :key="section.title">
        <section v-if="section.tab === tab" class="details-section">
          <h3 class="details-section__title">{{ section.title }}</h3>
          <UiDetailList :items="section.items" />
        </section>
      </template>
    </div>

    <!-- All tabs stay mounted while editing so Save validates every field. -->
    <v-form v-else ref="formRef" class="details-panel" @submit.prevent="save">
      <section
        v-for="section in SECTIONS"
        v-show="section.tab === tab"
        :key="section.title"
        class="details-section"
      >
        <h3 class="details-section__title">{{ section.title }}</h3>
        <div class="form-grid">
          <UiFormFields v-model:values="form" :fields="section.fields" />
        </div>
      </section>
    </v-form>

    <template #footer="{ close }">
      <template v-if="editing">
        <v-btn variant="outlined" @click="cancelEdit">Cancel</v-btn>
        <v-btn color="primary" :loading="busy" @click="save">Save changes</v-btn>
      </template>
      <template v-else>
        <v-btn variant="outlined" @click="close">Close</v-btn>
        <v-btn color="primary" prepend-icon="mdi-pencil-outline" @click="startEdit">Edit details</v-btn>
      </template>
    </template>
  </UiDialog>
</template>

<style scoped>
.details-tabs {
  margin-bottom: 16px;
}
.details-panel {
  min-height: 220px;
}
.details-section + .details-section {
  margin-top: 20px;
}
.details-section__title {
  margin: 0 0 12px;
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: var(--muted-foreground);
}
</style>
